import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, Typography } from '@material-ui/core';
import numeral from 'numeral';
import { DollarSign, TrendingUp, Users, ArrowRight, BarChart3 } from 'lucide-react';
import { useIntl } from 'react-intl';
import Button from '../../../../../../ui/canon/Button';
import Table from '../../../../../../ui/canon/Table';
import SingleColumnTemplate from '../../../../../../ui/layouts/SingleColumnTemplate';

const CfaHome = ({ cashFlows = [], company, currentUser, features = [] }) => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(-1);

  const cashFlowAnalysisWord = intl.locale === 'fr'
    ? (company?.cash_flow_analysis_word_fr || 'Analyse des flux de trésorerie')
    : (company?.cash_flow_analysis_word || 'Cash Flow Analysis');
  const dollarFormat = '$0,0.00';

  const handleOpen = (id) => {
    setOpen(true);
    setSelected(id);
  };

  const handleClose = () => {
    setOpen(false);
    setSelected(-1);
  };

  const columns = [
    {
      header: intl.formatMessage({ id: 'succession.cash_flow_analysis.home.column_name' }),
      accessor: "name",
      cell: (cashFlow) => (
        <div className="space-y-2">
          <a
            href={`/succession/calculators/cfa/${cashFlow.friendlyName}`}
            className="text-lg font-semibold text-succession-primary hover:opacity-80 no-underline"
          >
            {cashFlow.name}
          </a>
          <div className="text-sm text-succession-text-secondary">
            {cashFlow.numberOfBuyers} {cashFlow.numberOfBuyers > 1 ? intl.formatMessage({ id: 'succession.cash_flow_analysis.home.buyer_plural' }) : intl.formatMessage({ id: 'succession.cash_flow_analysis.home.buyer_singular' })}; {cashFlow.cashFlowLength} {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.year_cash_flow' })}
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-succession-bg-secondary text-succession-text-primary">
              {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.badge_seller_earnings' })} {numeral(cashFlow.sellerAverageCashFlow).format(dollarFormat)}
            </span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-succession-primary text-white">
              {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.badge_buyer_earnings' })} {numeral(cashFlow.buyer1AverageCashFlow).format(dollarFormat)}
            </span>
            {cashFlow.numberOfBuyers > 1 && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-succession-primary text-white">
                {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.badge_buyer2_earnings' })} {numeral(cashFlow.buyer2AverageCashFlow).format(dollarFormat)}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      header: intl.formatMessage({ id: 'succession.cash_flow_analysis.home.column_date_added' }),
      accessor: "createdAt",
      cell: (cashFlow) => new Intl.DateTimeFormat(intl.locale, { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(cashFlow.createdAt)),
    },
    {
      header: intl.formatMessage({ id: 'succession.cash_flow_analysis.home.column_actions' }),
      accessor: "actions",
      cell: (cashFlow) => (
        <div className="flex space-x-3">
          <a
            href={`/succession/calculators/cfa/${cashFlow.friendlyName}/results?print=true`}
            className="text-succession-primary hover:opacity-80 text-sm font-medium no-underline"
          >
            <i className="fas fa-download mr-1"></i> {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.action_download' })}
          </a>
          <a
            href={`/succession/calculators/cfa/${cashFlow.friendlyName}`}
            className="text-succession-primary hover:opacity-80 text-sm font-medium no-underline"
          >
            <i className="fas fa-pencil-alt mr-1"></i> {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.action_edit' })}
          </a>
          <button
            onClick={() => handleOpen(cashFlow.friendlyName)}
            className="text-succession-error hover:opacity-80 text-sm font-medium bg-none border-none cursor-pointer p-0"
          >
            <i className="fa fa-trash mr-1"></i> {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.action_delete' })}
          </button>
        </div>
      ),
    },
  ];

  const pageActions = (
    <Button
      as="a"
      href="/succession/calculators/cfa/start"
      variant="primary"
      size="md"
    >
      <i className="fas fa-plus mr-2"></i>
      {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.create_new_analysis' })}
    </Button>
  );

  if (cashFlows.length === 0) {
    return (
      <SingleColumnTemplate
        title={cashFlowAnalysisWord}
      >
        <div className="min-h-[calc(100vh-200px)] flex items-center justify-center p-8">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            {/* Icon/Visual */}
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-32 h-32 bg-gradient-to-br from-succession-primary/10 to-succession-secondary/10 rounded-full flex items-center justify-center">
                  <BarChart3 className="w-16 h-16 text-succession-primary" />
                </div>
                <div className="absolute -top-2 -right-2 w-12 h-12 bg-gradient-to-br from-succession-success/20 to-succession-success/10 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-succession-success" />
                </div>
              </div>
            </div>

            {/* Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl md:text-5xl font-bold text-succession-text-primary leading-tight">
                {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.empty_state_heading_1' })}
                <br />
                <span className="text-succession-primary">
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.empty_state_heading_2' })}
                </span>
              </h1>
              <p className="text-lg text-succession-text-secondary max-w-2xl mx-auto leading-relaxed">
                {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.empty_state_description' })}
              </p>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div className="bg-succession-bg-primary rounded-xl p-6 border border-succession-border">
                <div className="w-12 h-12 bg-succession-primary/10 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <DollarSign className="w-6 h-6 text-succession-primary" />
                </div>
                <h3 className="font-semibold text-succession-text-primary mb-2">
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.empty_state_benefit_1_heading' })}
                </h3>
                <p className="text-sm text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.empty_state_benefit_1_description' })}
                </p>
              </div>
              <div className="bg-succession-bg-primary rounded-xl p-6 border border-succession-border">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-semibold text-succession-text-primary mb-2">
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.empty_state_benefit_2_heading' })}
                </h3>
                <p className="text-sm text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.empty_state_benefit_2_description' })}
                </p>
              </div>
              <div className="bg-succession-bg-primary rounded-xl p-6 border border-succession-border">
                <div className="w-12 h-12 bg-succession-success/20 rounded-lg flex items-center justify-center mb-4 mx-auto">
                  <TrendingUp className="w-6 h-6 text-succession-success" />
                </div>
                <h3 className="font-semibold text-succession-text-primary mb-2">
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.empty_state_benefit_3_heading' })}
                </h3>
                <p className="text-sm text-succession-text-secondary">
                  {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.empty_state_benefit_3_description' })}
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="space-y-4">
              <Button
                as="a"
                href="/succession/calculators/cfa/start"
                size="lg"
                className="inline-flex items-center gap-3 px-10 py-5 bg-succession-primary hover:bg-succession-primary-hover text-white rounded-xl font-semibold transition-all text-lg shadow-succession-lg hover:shadow-succession-xl transform hover:scale-105"
              >
                <BarChart3 className="w-6 h-6" />
                {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.create_first_analysis' })}
                <ArrowRight className="w-5 h-5" />
              </Button>
              <p className="text-sm text-succession-text-secondary">
                {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.takes_about' })}
              </p>
            </div>
          </div>
        </div>
      </SingleColumnTemplate>
    );
  }

  return (
    <SingleColumnTemplate
      title={cashFlowAnalysisWord}
      subtitle={intl.formatMessage({ id: 'succession.cash_flow_analysis.home.page_subtitle' })}
      actions={pageActions}
    >
      <Table
        columns={columns}
        data={cashFlows}
        rowKey="friendlyName"
      />

      {/* Delete Modal */}
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        BackdropProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          }
        }}
      >
        <Box className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-lg border-2 border-gray-200 shadow-succession-lg p-6">
          <Typography id="modal-modal-title" variant="h6" component="h2" className="mb-4 text-gray-900 font-semibold">
            {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.confirm_delete_title' })}
          </Typography>
          <Typography id="modal-modal-description" className="mb-6 text-gray-600">
            {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.confirm_delete_body' })}
          </Typography>
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              onClick={handleClose}
            >
              {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.cancel' })}
            </Button>
            <Button
              as="a"
              variant="primary"
              href={`/succession/calculators/cfa/${selected}/delete`}
              className="!bg-red-600 hover:!bg-red-700"
            >
              {intl.formatMessage({ id: 'succession.cash_flow_analysis.home.action_delete' })}
            </Button>
          </div>
        </Box>
      </Modal>
    </SingleColumnTemplate>
  );
};

CfaHome.propTypes = {
  cashFlows: PropTypes.array,
  company: PropTypes.object,
  currentUser: PropTypes.object,
  features: PropTypes.array,
};

CfaHome.defaultProps = {
  cashFlows: [],
  company: {},
  currentUser: {},
  features: [],
};

export default CfaHome;
